<script setup lang="ts">
/**
 * An order saved "ยังไม่โอน · ส่ง QR ให้ลูกค้า" (api/app/pos_pending.py): the POS keeps it as a held bill (พักบิล) — no POS
 * order, no stock, not in the kitchen — until "ยืนยันชำระ". Shows the amount, the website's payment deadline (21:30 the day
 * before delivery; nothing happens by itself after it), "ส่งใบสรุปยอดและ QR ในแชท" (the summary + the PromptPay QR for the
 * exact amount go into the composer; staff press send) and the confirm form (the transfer from the customer's newest
 * checked slip; an amount that is not the total needs a second click; a new delivery day when it is late) and
 * "แก้ไขออเดอร์" (owner 18 ต.ค. 69 "พักบิลสามารถแก้ไขออเดอร์ได้": the order tab's form prefilled, the SAME held bill in the POS).
 * An edit after the summary + QR went to the customer (any change — the customer's summary is old): "แก้ออเดอร์แล้ว ส่งใบสรุปยอดและ QR ใหม่ให้ลูกค้า"
 * (nothing is sent by itself). "ยกเลิกพักบิล" (owner 18 ต.ค. 69 decision 7: switching to a card link cancels the held bill first —
 * removed like on the POS sale page; a second click confirms).
 */
import { computed, ref, watch } from 'vue'
import type { ChatOrderRef, PosOrderResult } from '../../../api/pos/types'
import { baht, clockTime, dayKey } from '../../../lib/inbox'
import { bangkokTime, deliveryDateLabel, deliveryOf, holdDueText, menuOf, pendingBillText, slipPayment, slotLabel } from '../../../lib/order'
import { useClock } from '../../../stores/clock'
import { useInbox } from '../../../stores/inbox'
import { useOrders } from '../../../stores/order'
import Icon from '../../ui/Icon.vue'

const props = defineProps<{ convId: string; order: ChatOrderRef; result?: PosOrderResult | null; saved?: boolean }>()
const orders = useOrders()
const inbox = useInbox()
const clock = useClock()

const total = computed(() => props.order.total ?? 0)
const due = computed(() => holdDueText(props.order.hold))
const fetched = ref<PosOrderResult | null>(null)
const quote = computed(() => props.result?.order.order ?? fetched.value?.order.order ?? null)
// an edit made another cart: the plan fetched for the summary text belongs to the old one
watch(
  () => props.order.hold?.edit_count ?? 0,
  () => (fetched.value = null),
)
/** "Pro 7 day 21 มื้อ · 21 มื้อ · ส่ง จ. 5 ต.ค. 10:00 - 12:00" (the held cart when the POS plan is not loaded) */
const summary = computed(() => {
  const q = quote.value
  const h = props.order.hold
  const c = h?.cart
  const parts: string[] = []
  if (q) parts.push(`${q.set_menu.name} · ${q.meals} มื้อ`)
  else if (c) {
    const name = orders.catalog?.set_menus.find((m) => m.id === c.set_menu_id)?.name ?? 'แพ็ก'
    const meals = c.items.reduce((a, i) => a + (menuOf(orders.catalog, i.menu_id)?.type === 'snacks' ? 0 : i.qty), 0)
    parts.push(`${name} · ${meals} มื้อ`)
  }
  const date = q?.delivery.date ?? h?.delivery_date
  if (date) {
    const dhl = (q?.shipping.carrier ?? h?.shipping) === 'dhl'
    const slot = dhl ? 'DHL' : slotLabel(orders.catalog, q?.delivery.time ?? h?.delivery_time ?? null)
    parts.push(`ส่ง ${deliveryDateLabel(date)}${slot ? ` ${slot}` : ''}`)
  }
  return parts.join(' · ')
})

/** "แก้ไขแล้ว 1 ครั้ง · ล่าสุด 14:20 น. โดย พลอย" */
const edited = computed(() => {
  const h = props.order.hold
  if (!h?.edit_count) return ''
  const at = h.edited_at ? ` · ล่าสุด ${clockTime(h.edited_at)} น.` : ''
  return `แก้ไขแล้ว ${h.edit_count} ครั้ง${at}${h.edited_by_name ? ` โดย ${h.edited_by_name}` : ''}`
})

// ------------------------------------------------ แก้ไขออเดอร์ (the order tab's form, prefilled — stores/order.ts startEdit)
const editErr = ref<string | null>(null)
function edit(): void {
  editErr.value = null
  if (!orders.startEdit(props.convId, props.order)) {
    editErr.value = 'ออเดอร์นี้แก้จากแชทไม่ได้ (ไม่มีข้อมูลตะกร้าเดิม) แก้ที่หน้าขาย POS'
  }
}

// ------------------------------------------------ ส่งใบสรุปยอดและ QR ในแชท
const qrBusy = computed(() => !!orders.qrBusy[props.order.id])
const qrErr = ref<string | null>(null)
async function sendQr(): Promise<void> {
  qrErr.value = null
  let res = props.result ?? fetched.value
  if (!res?.order.order) {
    try {
      fetched.value = await orders.loadChatOrder(props.order.id)
      res = fetched.value
    } catch {
      /* the text goes without the pack line */
    }
  }
  const bill = res ?? { chat_order: props.order, order: {}, replayed: false }
  try {
    const qr = await orders.stageQr(props.convId, props.order.id)
    const bank = qr.payment_text?.trim() ?? ''
    orders.fillComposer(props.convId, pendingBillText(bill, orders.catalog, bank))
    inbox.flash(
      bank
        ? 'ใส่ใบสรุปยอด เลขบัญชี และ QR พร้อมเพย์ในช่องพิมพ์แล้ว ตรวจแล้วกดส่ง'
        : 'ใส่ใบสรุปยอดและ QR พร้อมเพย์ในช่องพิมพ์แล้ว (ยังไม่มีเลขบัญชี: ให้เจ้าของใส่ที่ ตั้งค่า > ข้อความแจ้งการชำระเงิน)',
    )
  } catch (e) {
    orders.fillComposer(props.convId, pendingBillText(bill, orders.catalog))
    qrErr.value = `แนบ QR ไม่สำเร็จ: ${e instanceof Error ? e.message : String(e)}`
    inbox.flash('ใส่ใบสรุปยอดในช่องพิมพ์แล้ว แต่แนบ QR ไม่สำเร็จ')
  }
}

// ------------------------------------------------ ยกเลิกพักบิล (a second click confirms)
const cancelArmed = ref(false)
const cancelErr = ref<string | null>(null)
const cancelling = computed(() => orders.cardBusy[props.order.id] === 'cancel')
async function cancelHold(): Promise<void> {
  cancelErr.value = null
  if (!cancelArmed.value) {
    cancelArmed.value = true
    return
  }
  try {
    await orders.cancelOrder(props.convId, props.order.id)
    inbox.flash('ยกเลิกพักบิลแล้ว (ลบจากรายการบิลที่พักของ POS)')
  } catch (e) {
    cancelErr.value = e instanceof Error ? e.message : String(e)
  } finally {
    cancelArmed.value = false
  }
}

// ------------------------------------------------ ยืนยันชำระ
const payOpen = ref(false)
const slip = computed(() => slipPayment(inbox.threads[props.convId] ?? []))
const pay = ref({ date: dayKey(clock.now), time: bangkokTime(clock.now), amount: String(total.value), from: 'none' as 'slip' | 'staff' | 'none' })
// an edit changed the total: the amount nobody typed follows it
watch(total, (t) => {
  if (pay.value.from === 'none') pay.value = { ...pay.value, amount: String(t) }
})
watch(
  [slip, payOpen],
  ([s]) => {
    if (!s || pay.value.from === 'staff') return
    pay.value = { date: s.date, time: s.time, amount: s.amount || String(total.value), from: 'slip' }
  },
  { immediate: true },
)
function typed(patch: Partial<{ date: string; time: string; amount: string }>): void {
  pay.value = { ...pay.value, ...patch, from: 'staff' }
  armed.value = false
}
const amountNum = computed(() => Number(pay.value.amount))
const diff = computed(() => (Number.isFinite(amountNum.value) ? Math.round((amountNum.value - total.value) * 100) / 100 : 0))
const valid = computed(
  () => /^\d{4}-\d{2}-\d{2}$/.test(pay.value.date) && /^\d{2}:\d{2}$/.test(pay.value.time) && amountNum.value > 0,
)
/** The second click ("ยืนยันทั้งที่ยอดไม่ตรง") is armed: the amount is not the order total. */
const armed = ref(false)
const payErr = ref<string | null>(null)
const busy = computed(() => !!orders.payBusy[props.order.id])

// a new delivery day (late, or the customer asks): like the web, staff move the date when the order is made
const delivery = computed(() => deliveryOf(orders.catalog, clock.now))
const redate = ref(false)
watch(due, (d) => (redate.value = redate.value || !!d?.late), { immediate: true })
const newDate = ref('')
const newTime = ref('')
const isDhl = computed(() => (quote.value?.shipping.carrier ?? props.order.hold?.shipping) === 'dhl')
watch(redate, (on) => {
  if (on && !newDate.value) newDate.value = delivery.value.default_date
  if (on && !newTime.value) newTime.value = props.order.hold?.delivery_time ?? delivery.value.default_time
})

async function confirm(): Promise<void> {
  payErr.value = null
  if (!valid.value || busy.value) return
  if (diff.value !== 0 && !armed.value) {
    armed.value = true
    return
  }
  const body = {
    paid_at: `${pay.value.date} ${pay.value.time}`,
    amount: amountNum.value,
    slip_message_id: pay.value.from === 'slip' ? (slip.value?.messageId ?? null) : null,
    accept_diff: armed.value && diff.value !== 0,
    ...(redate.value && newDate.value ? { delivery_date: newDate.value, delivery_time: isDhl.value ? null : newTime.value || null } : {}),
  }
  const out = await orders.confirmPay(props.convId, props.order.id, body)
  if (out.kind === 'done') {
    payOpen.value = false
    armed.value = false
    inbox.flash(`ยืนยันชำระแล้ว ออกใบ ${out.order.order_code ?? ''} ใน POS`.trim())
  } else if (out.kind === 'mismatch') {
    armed.value = true
    payErr.value = out.message
  } else {
    payErr.value = out.message
  }
}
</script>

<template>
  <article class="ho" :class="{ late: due?.late, big: saved }" aria-label="ออเดอร์รอชำระ">
    <div class="ho-h">
      <span class="ho-chip"><Icon name="clock" />รอชำระ</span>
      <b class="ho-amt mono">{{ baht(total) }}</b>
      <span class="grow"></span>
      <span v-if="order.hold?.code" class="ho-code mono" title="รหัสพักบิลในหน้าขาย POS">พักบิล {{ order.hold.code }}</span>
    </div>
    <div class="ho-sumrow">
      <p class="ho-sum">{{ summary }}</p>
      <button class="tlink ac ho-edit" :disabled="busy || qrBusy" @click="edit"><Icon name="pencil" />แก้ไขออเดอร์</button>
    </div>
    <p v-if="edited" class="ho-ed">{{ edited }}</p>
    <p v-if="order.hold?.qr_resend" class="ho-resend" role="status">
      <Icon name="alert" /><span>แก้ออเดอร์แล้ว ส่งใบสรุปยอดและ QR ใหม่ให้ลูกค้า</span>
    </p>
    <p v-if="editErr" class="err" role="alert"><Icon name="alertCircle" /><span>{{ editErr }}</span></p>
    <p v-if="due" class="ho-due" :class="{ late: due.late }">
      <Icon :name="due.late ? 'alertCircle' : 'calendar'" /><span>{{ due.text }}</span>
    </p>
    <div class="ho-acts">
      <button class="btn2" :disabled="qrBusy" @click="sendQr">
        <span v-if="qrBusy" class="spin"></span><Icon v-else name="qrCode" />{{
          order.hold?.qr_resend ? 'ส่งใบสรุปยอดและ QR ใหม่ในแชท' : 'ส่งใบสรุปยอดและ QR ในแชท'
        }}
      </button>
      <button v-if="!payOpen" class="btn-save" @click="payOpen = true"><Icon name="checkCircle" />ยืนยันชำระ</button>
    </div>
    <p v-if="qrErr" class="err" role="alert"><Icon name="alertCircle" /><span>{{ qrErr }}</span></p>
    <p v-if="!payOpen" class="ho-cancel">
      <template v-if="cancelArmed">ยกเลิกแล้ว QR เดิมใช้ยืนยันออเดอร์นี้ไม่ได้ (ไม่มีออเดอร์เกิดขึ้น) ·</template>
      <button class="tlink" :class="{ armed: cancelArmed }" :disabled="busy || cancelling" @click="cancelHold">
        <span v-if="cancelling" class="spin"></span><Icon v-else name="x" />{{ cancelArmed ? 'ยืนยันยกเลิกพักบิล' : 'ยกเลิกพักบิล (ลูกค้าเปลี่ยนวิธีชำระ)' }}
      </button>
      <button v-if="cancelArmed" class="tlink" @click="cancelArmed = false">ไม่ยกเลิก</button>
    </p>
    <p v-if="cancelErr" class="err" role="alert"><Icon name="alertCircle" /><span>{{ cancelErr }}</span></p>

    <div v-if="payOpen" class="ho-pay" role="group" aria-label="ยืนยันชำระ">
      <div class="ho-g3">
        <label class="fl"
          ><span class="fl-l">วันที่โอน</span
          ><span class="inp"><input type="date" :value="pay.date" aria-label="วันที่โอน" @change="typed({ date: ($event.target as HTMLInputElement).value })" /></span
        ></label>
        <label class="fl"
          ><span class="fl-l">เวลา</span
          ><span class="inp"><input type="time" :value="pay.time" aria-label="เวลาที่โอน" @change="typed({ time: ($event.target as HTMLInputElement).value })" /></span
        ></label>
        <label class="fl"
          ><span class="fl-l">ยอดโอน</span
          ><span class="inp" :class="{ bad: diff !== 0 }"
            ><input
              :value="pay.amount"
              type="text"
              inputmode="decimal"
              aria-label="ยอดโอน บาท"
              @input="typed({ amount: ($event.target as HTMLInputElement).value })" /></span
        ></label>
      </div>
      <p v-if="pay.from === 'slip' && slip" class="note">
        วัน เวลา และยอดจากสลิปที่ลูกค้าส่งในแชท ({{ slip.label }}<template v-if="slip.bank"> · {{ slip.bank }}</template>)
      </p>
      <p v-else-if="!slip" class="note">ยังไม่มีสลิปที่ตรวจแล้วในแชทนี้ ใส่วัน เวลา และยอดตามสลิปเอง</p>
      <p v-if="diff !== 0 && valid" class="err" role="alert">
        <Icon name="alertCircle" /><span
          >ยอดโอน{{ diff > 0 ? 'เกิน' : 'ขาด' }}ยอดที่ต้องชำระ {{ baht(Math.abs(diff)) }} ตรวจสลิปอีกครั้ง{{
            armed ? ' ถ้าถูกต้องแล้ว กดยืนยันอีกครั้ง' : ''
          }}</span
        >
      </p>
      <label class="ho-chk"><input v-model="redate" type="checkbox" /><span>เลือกวันส่งใหม่</span></label>
      <div v-if="redate" class="ho-g2">
        <label class="fl"
          ><span class="fl-l">วันส่ง</span
          ><span class="inp"
            ><input v-model="newDate" type="date" :min="delivery.date_min" :max="delivery.date_max" aria-label="วันส่งใหม่" /></span
        ></label>
        <label v-if="!isDhl" class="fl"
          ><span class="fl-l">รอบส่ง</span
          ><span class="inp"
            ><select v-model="newTime" aria-label="รอบส่งใหม่">
              <option v-for="s in delivery.slots" :key="s.time" :value="s.time">{{ s.label }}</option>
            </select></span
          ></label
        >
      </div>
      <div class="ho-acts">
        <button class="btn2" :disabled="busy" @click="payOpen = false">ยกเลิก</button>
        <button class="btn-save" :class="{ second: armed && diff !== 0 }" :disabled="busy || !valid" @click="confirm">
          <span v-if="busy" class="spin"></span><Icon v-else name="checkCircle" />{{
            busy ? 'กำลังยืนยัน…' : armed && diff !== 0 ? 'ยืนยันทั้งที่ยอดไม่ตรง' : 'ยืนยันชำระ'
          }}
        </button>
      </div>
      <p v-if="payErr" class="err" role="alert"><Icon name="alertCircle" /><span>{{ payErr }}</span></p>
      <p class="note">กดแล้ว POS ออกใบจากพักบิลนี้ทันที (ตัดสต๊อก เข้าครัว)</p>
    </div>
  </article>
</template>

<style scoped>
.ho {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid #f0d9a8;
  border-radius: 12px;
  background: #fffaf0;
  text-align: left;
}
.ho.late {
  border-color: #efb0a9;
  background: #fff6f5;
}
.ho-h {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.ho-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 22px;
  padding: 1px 8px;
  border-radius: 6px;
  background: var(--warns);
  color: #7a4700;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
}
.ho-chip :deep(.ic) {
  width: 13px;
  height: 13px;
}
.ho-amt {
  font-size: 15px;
  line-height: 22px;
  font-weight: 600;
}
.ho.big .ho-amt {
  font-size: 18px;
}
.ho-code {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  white-space: nowrap;
}
.ho-sumrow {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.ho-sum {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 13px;
  line-height: 20px;
  color: #3e4d46;
}
.ho-edit {
  flex: none;
  white-space: nowrap;
}
.ho-ed {
  margin: -4px 0 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
/* an edit after the QR went out: the staff must send the new one (nothing goes by itself) */
.ho-resend {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0;
  padding: 7px 10px;
  border: 1px solid #efb0a9;
  border-radius: 8px;
  background: #fff1ef;
  color: #a61b12;
  font-size: 12.5px;
  line-height: 19px;
  font-weight: 600;
}
.ho-resend :deep(.ic) {
  width: 14px;
  height: 14px;
  flex: none;
  margin-top: 2.5px;
}
.ho-due {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0;
  font-size: 12.5px;
  line-height: 19px;
  color: #6b4300;
}
.ho-due.late {
  color: #a61b12;
  font-weight: 600;
}
.ho-due :deep(.ic) {
  width: 14px;
  height: 14px;
  flex: none;
  margin-top: 2.5px;
}
.ho-acts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.ho-acts .btn2,
.ho-acts .btn-save {
  height: 38px;
  font-size: 13px;
}
.ho-acts .btn2 {
  flex: 1 1 auto;
}
.ho-acts .btn-save.second {
  background: #b25e00;
}
.ho-acts .btn-save.second:hover:not(:disabled) {
  background: #8f4b00;
}
.ho-pay {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px dashed #ead6ad;
}
.ho-g3 {
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(0, 0.9fr) minmax(0, 1fr);
  gap: 8px;
}
.ho-g2 {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 8px;
}
.ho-cancel {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
  margin: -2px 0 0;
  font-size: 12px;
  line-height: 18px;
  color: #6b3a35;
}
.ho-cancel .tlink {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--mut);
}
.ho-cancel .tlink.armed {
  color: #a61b12;
  font-weight: 600;
}
.ho-chk {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  line-height: 19px;
  color: #3e4d46;
  cursor: pointer;
}
</style>
